import React, { useEffect } from "react";
import { X } from "@phosphor-icons/react";

/**
 * Fully responsive scrollable modal shell with sticky header + footer.
 * Mobile: full-screen. Desktop: max-w with vertical scroll.
 * Pass footer as a prop to keep action buttons visible while content scrolls.
 * variant="dark" renders the premium near-black panel (used by the player
 * add/edit popup) — default stays the light panel used across the admin.
 */
export default function Modal({ open, onClose, title, subtitle, size = "md", children, footer, testId, variant }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const dark = variant === "dark";
  const sizeCls = size === "xl" ? "sm:max-w-6xl" : size === "lg" ? "sm:max-w-4xl" : size === "sm" ? "sm:max-w-md" : "sm:max-w-2xl";

  return (
    <div
      className={`fixed inset-0 ${dark ? "bg-black/70 backdrop-blur-md" : "bg-black/80 backdrop-blur-sm"} flex items-start justify-center overflow-y-auto`}
      style={{ zIndex: 100 }}
      onClick={onClose}
      data-testid={testId ? `${testId}-backdrop` : undefined}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${sizeCls} ${dark
          ? "bg-[#0b0e13]/95 border border-white/15 text-slate-100 shadow-[0_28px_90px_rgba(0,0,0,.65)] sm:rounded-xl"
          : "bg-slate-50 border border-gray-200 text-slate-900 sm:rounded-md"} flex flex-col min-h-full sm:min-h-0 sm:my-4 sm:mx-4`}
        data-testid={testId}
      >
        <div className={`p-5 sm:p-6 ${dark ? "border-b border-white/10" : "border-b border-gray-100"} flex items-start justify-between gap-3 flex-shrink-0`}>
          <div className="min-w-0">
            {subtitle && <div className={`label-mono mb-1 ${dark ? "!text-slate-400" : ""}`}>{subtitle}</div>}
            <h3 className="font-display text-2xl sm:text-3xl uppercase leading-tight truncate">{title}</h3>
          </div>
          <button onClick={onClose} className={`p-2 rounded flex-shrink-0 ${dark ? "text-slate-400 hover:text-white hover:bg-white/10" : "hover:bg-white text-gray-500"}`} data-testid="modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-6 flex-1">{children}</div>

        {footer && (
          <div className={`p-4 sm:p-5 flex justify-end gap-2 flex-wrap flex-shrink-0 sticky bottom-0 z-10 ${dark ? "border-t border-white/10 bg-[#0b0e13]" : "border-t border-gray-100 bg-slate-50"}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
